import React, { useState, useEffect, useRef } from 'react';
import { runHealthCheck } from '../services/api';
import FindingsView from './health/FindingsView';
import InsightsView from './health/InsightsView';
import ClientsOverview from './health/ClientsOverview';
import HealthSettings from './health/HealthSettings';
import { downloadHealthReport, downloadInsightsReport } from './health/reportDownload';

const UNAVAILABLE_TEXT = {
  permission: 'Some Xero reports were not accessible — sign out and reconnect Xero to allow report access. Checks and insights that need them were skipped.',
  unrecognised: 'Xero returned a report in an unexpected layout — checks and insights that need it were skipped.'
};

const TABS = [['checks', 'Checks'], ['insights', 'Insights'], ['clients', 'All clients'], ['settings', 'Settings']];

export default function HealthCheckPanel({ tenantId, clientName, onSelectClient }) {
  const [tab, setTab] = useState('checks');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const currentTenant = useRef(tenantId);

  // Results belong to one organisation — clear them when the client changes.
  useEffect(() => {
    currentTenant.current = tenantId;
    setResult(null);
    setError(null);
    setLoading(false);
    setJobStatus(null);
  }, [tenantId]);

  async function run() {
    const requestedFor = tenantId;
    setLoading(true);
    setError(null);
    setJobStatus({ status: 'pending' });
    try {
      const data = await runHealthCheck(requestedFor, status => {
        if (currentTenant.current === requestedFor) setJobStatus(status);
      });
      if (currentTenant.current === requestedFor) setResult(data);
    } catch (err) {
      if (currentTenant.current === requestedFor) {
        setError(err.response?.data?.error || err.message || 'Health check failed');
      }
    } finally {
      if (currentTenant.current === requestedFor) {
        setLoading(false);
        setJobStatus(null);
      }
    }
  }

  const findings = result?.findings || [];
  const reasons = [...new Set((result?.unavailable || []).map(u => u.reason))];
  const showRun = tab === 'checks' || tab === 'insights';

  return (
    <div className="hc-panel">
      <div className="rules-header">
        <div>
          <h2 className="rules-title">Bookkeeping health check</h2>
          <p className="rules-sub">
            Read-only review of Xero data for {tab === 'clients' ? 'all your connected organisations' : (clientName || 'this organisation')}.
            Nothing is changed in Xero.
          </p>
        </div>
        {showRun && (
          <button className="btn-primary" onClick={run} disabled={loading || !tenantId}>
            {loading ? 'Checking…' : result ? 'Run again' : 'Run health check'}
          </button>
        )}
      </div>

      <div className="tabs">
        {TABS.map(([k, label]) => (
          <button key={k} className={`tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>

      {tab === 'clients' && (
        <ClientsOverview onOpen={(id) => { onSelectClient(id); setTab('checks'); }} />
      )}
      {tab === 'settings' && <HealthSettings tenantId={tenantId} />}

      {showRun && loading && (
        <div className="running-state">
          <div className="spinner" />
          <div>
            <div className="running-title">
              {jobStatus?.status === 'pending'
                ? (jobStatus.queuePosition ? `Waiting in queue (position ${jobStatus.queuePosition})…` : 'Joining the health-check queue…')
                : 'Reviewing Xero data…'}
            </div>
            <div className="running-sub">
              {jobStatus?.status === 'pending'
                ? 'Checks run one at a time to protect the Xero connection. You can safely leave this page; the result is saved when complete.'
                : 'Reading Xero data now. You can safely leave this page; the completed result will be saved for later download.'}
            </div>
          </div>
        </div>
      )}

      {showRun && error && !loading && (
        <div className="error-state">
          <div className="error-title">Health check failed</div>
          <div className="error-msg">{error}</div>
        </div>
      )}

      {showRun && !result && !loading && !error && (
        <div className="idle-state">
          <div className="idle-title">Run a health check</div>
          <div className="idle-sub">Checks the last 12 months for bookkeeping errors and works out key financial insights.</div>
        </div>
      )}

      {showRun && result && !loading && (
        <>
          <div className="stats-bar">
            <div className="stat-card">
              <div className="stat-label">Health score</div>
              <div className={`stat-value hc-score-${result.rating}`}>{result.score === null ? '—' : `${result.score}%`}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Action needed</div>
              <div className="stat-value">{findings.filter(f => f.status === 'fail').length}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">To review</div>
              <div className="stat-value">{findings.filter(f => f.status === 'warn').length}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Transactions checked</div>
              <div className="stat-value">{(result.counts?.bankTransactions ?? 0) + (result.counts?.invoices ?? 0)}</div>
            </div>
          </div>

          {reasons.map(r => <div key={r} className="hc-note">{UNAVAILABLE_TEXT[r] || 'Some data was unavailable.'}</div>)}
          {(result.truncated || []).length > 0 && (
            <div className="hc-note">Very large organisation — only the most recent items were checked ({result.truncated.join(', ')}).</div>
          )}
          <div className="hc-report-actions">
            <div>
              <div className="hc-report-title">Download this completed review</div>
              <div className="hc-report-sub">Downloads are self-contained HTML reports that can be saved, shared securely, or printed to PDF.</div>
            </div>
            <div className="hc-report-buttons">
              <button type="button" className="btn-secondary" onClick={() => downloadHealthReport(result, clientName)}>
                Download health report
              </button>
              <button type="button" className="btn-secondary" onClick={() => downloadInsightsReport(result, clientName)}>
                Download insights report
              </button>
            </div>
          </div>

          {tab === 'checks' ? (
            <>
              <FindingsView findings={findings} />
              <div className="hc-note hc-note-muted">
                Xero's API doesn't expose bank feed lines that haven't been processed yet — use the bank balance check
                (Settings) to confirm each account agrees to the statement.
              </div>
            </>
          ) : <InsightsView result={result} />}
        </>
      )}
    </div>
  );
}
